import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type ThemeName = 'dark' | 'light';

export const THEME_DARK_CLASS = 'theme-slate-cyber';
export const THEME_LIGHT_CLASS = 'theme-arctic-executive';
export const THEME_MANAGED_CLASS = 'theme-managed';

const THEME_STORAGE_KEY = 'nexgent-theme';

interface ThemeContextValue {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const applyThemeToDocument = (theme: ThemeName) => {
  const root = document.documentElement;
  root.classList.remove(THEME_DARK_CLASS, THEME_LIGHT_CLASS);
  root.classList.add(theme === 'dark' ? THEME_DARK_CLASS : THEME_LIGHT_CLASS);
  root.setAttribute('data-nx-theme', theme);
  root.setAttribute('data-nx-family', theme);
};

const getInitialTheme = (): ThemeName => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light') return 'light';
  } catch (error) {
    console.warn('Theme storage read failed', error);
  }
  return 'dark';
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(getInitialTheme);

  useEffect(() => {
    applyThemeToDocument(theme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (error) {
      console.warn('Theme storage write failed', error);
    }
  }, [theme]);

  const setTheme = useCallback((next: ThemeName) => {
    setThemeState(next === 'light' ? 'light' : 'dark');
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}